import { formatDisplayName } from '@/common/utils/formatDisplayName';
import { getClientLabels } from '@/views/Buyers/utils';
import type { ClientGroup, ClientGroupContact } from './types';

export function getClientsLabelsFromClientGroup(
  contacts: ClientGroup['contacts'],
) {
  const clients: ClientGroupContact[] = contacts.filter(
    (c: ClientGroupContact) => c.type === 'client',
  );

  const clientsLabels = clients
    .map((client) => ({
      id: client.id,
      ...getClientLabels(client),
    }))
    .filter(({ label }) => !!label);

  return clientsLabels;
}

/**
 * Formats a client group's contacts into a display string.
 * @param contacts - Array of client group contacts
 * @returns Formatted string of client names
 */
export function formatClientGroupNameString(contacts: ClientGroup['contacts']) {
  const clientLabels = getClientsLabelsFromClientGroup(contacts);

  return clientLabels
    .map(({ label, secondaryLabel }, i) => {
      const parts: string[] = [];
      if (i > 0) {
        parts.push(', ');
      }
      if (label) {
        parts.push(label);
      }
      if (secondaryLabel) {
        parts.push(` (${secondaryLabel})`);
      }
      return parts.join('');
    })
    .join('');
}

/**
 * Formats a client group's contacts into a display string with agent names appended.
 * @param contacts - Array of client group contacts
 * @returns Formatted string of client names with agent names in parentheses
 */
export function formatClientGroupNameStringWithAgents(
  contacts: ClientGroup['contacts'],
) {
  const clientNames = formatClientGroupNameString(contacts);

  const agents: ClientGroupContact[] = contacts.filter(
    (c: ClientGroupContact) => c.type === 'agent',
  );

  const agentNames = agents
    .map(formatDisplayName)
    .filter((name) => name)
    .join(', ');

  if (agentNames) {
    return clientNames ? `${clientNames} (${agentNames})` : agentNames;
  }

  return clientNames;
}
